/**
 * The last line of defence before a span leaves the process: no request or
 * response header, and no credential-shaped value, is ever exported.
 *
 * ═══ THE INCIDENT (2026-09-10, P0) ═══
 *
 * `@effect/platform`'s `HttpMiddleware.tracer` — applied to EVERY
 * `toWebHandler` route by `HttpApp.toHandled` — stamps every request header
 * on the `http.server` span as `http.request.header.<name>`, redacting only
 * `authorization`, `cookie`, `set-cookie` and `x-api-key`. In production that
 * was ~40 headers per RPC call, including `x-clerk-auth-token` (a live Clerk
 * session JWT), `x-clerk-auth-signature`, `x-clerk-request-data` and
 * `x-cova-user-id` — ~145k spans a day, for at least a week, into SigNoz.
 *
 * ═══ WHY AT THE EXPORTER ═══
 *
 * Header redaction at the Effect level (`redact-request-headers.ts`) stops
 * the plaintext ever reaching an Effect span. This module is the second,
 * independent layer: it runs on the OTEL side, so it also covers spans Effect
 * never sees (`@vercel/otel`'s request and fetch spans, any future
 * instrumentation) and any attribute that is merely credential-SHAPED. It is
 * applied by wrapping the `SpanExporter`, not as a `SpanProcessor`: a
 * processor's `onStart` runs before `@effect/opentelemetry` copies attributes
 * onto the OTEL span, and `onEnd` order among processors is not something
 * `@vercel/otel` lets us control. The exporter is the one choke point every
 * exported span passes through, in every runtime.
 *
 * ═══ WHAT IT DOES ═══
 *
 *   1. DROPS every `http.request.header.*` / `http.response.header.*` key.
 *      All of them, not a denylist: headers are the transport's business, and
 *      the two we did want (`user-agent`, the client address) are already on
 *      the span under their own semconv names.
 *   2. REDACTS the value of any key that names a credential
 *      (`authorization`, `cookie`, `password`, `secret`, `api-key`,
 *      `access_token`, …). Deliberately NOT a bare `token` match:
 *      `gen_ai.usage.input_tokens` is a number we want.
 *   3. REDACTS any string value shaped like a JWT or a `Bearer …` header,
 *      whatever key it sits under.
 *   4. CUTS any presigned URL (one carrying `X-Amz-Signature=` or
 *      `X-Amz-Credential=`) down to its host, in place, whatever key it sits
 *      under and wherever it sits in the string. A presigned URL is a bearer
 *      credential until it expires: the AgentCore live-view stream URL and
 *      the replay screenshot GETs both are (spec 2026-09-29-001 §6.6). The
 *      host stays because "which endpoint" is what a reader needs; the path
 *      names the org and session, the query is the credential. The same
 *      cut applies to the span's name, status message and events.
 *   5. REPLACES a browser tool's raw parameters on `@effect/ai`'s
 *      `Toolkit.handle` span. That span annotates `tool` and `parameters`
 *      with the call's arguments as the model wrote them, and for a browser
 *      tool those are a URL with its path and query, the text the worker
 *      typed, form values and the worker's report (spec 2026-09-29-001 §8
 *      rule 2). Other tools' parameters stay: their arguments are this
 *      span's point, and nothing about them is in scope here.
 *   6. REDACTS any string value carrying a Stripe key, publishable key,
 *      ephemeral key, webhook secret or `*_secret_*` client secret (the
 *      read-only gateway's key pattern, spec billing/2026-10-07-001 03 §2.4),
 *      whatever key it sits under; in the span name and status message only
 *      the match is replaced.
 *   7. STRIPS the query string and fragment from any `api.stripe.com` URL, in
 *      place, wherever it sits: a Stripe query carries customer filters and
 *      search text (03 §4, README L9). The path stays.
 *   8. TEMPLATES the path of every URL attribute (`URL_ATTRIBUTE_KEYS`:
 *      `http.target`, `url.path`, `url.full`, …): an org slug under `/o/` and
 *      every record id become `[orgSlug]` / `[id]` (spec multi-product 011
 *      §2, R-011-2; `route-template.ts`). Next.js and fetch instrumentation
 *      record the raw path, which names the firm and the record.
 *
 * A scrubbed span is a shallow CLONE with the same prototype (so
 * `duration`/`ended` getters keep working for the OTLP transformer) and a new
 * `attributes` object; the original is left alone for any other processor
 * that still holds it.
 *
 * Isomorphic: this module ships in the browser bundle too. No Node imports.
 */
import type { AttributeValue, Attributes } from "@opentelemetry/api"
import type { ReadableSpan, SpanExporter } from "@opentelemetry/sdk-trace-base"

import { routeTemplateOf, URL_ATTRIBUTE_KEYS } from "./route-template"

/** `@opentelemetry/core`'s `ExportResult`, without adding a direct dependency on it. */
type ExportResultCallback = Parameters<SpanExporter["export"]>[1]

/** Every transport header attribute, request and response side. */
export const HEADER_ATTRIBUTE_KEY = /^http\.(request|response)\.header\./

/**
 * Attribute keys that name a credential. Word-bounded on `.`, `_`, `-` so a
 * key like `gen_ai.usage.input_tokens` or `gen_ai.response.first_token_ms`
 * is untouched — the match needs a WHOLE segment such as `access_token`.
 */
export const SECRET_ATTRIBUTE_KEY =
  /(^|[._-])(authorization|cookie|set-cookie|password|passwd|secret|api[-_]?key|ingestion[-_]?key|access[-_]?token|refresh[-_]?token|id[-_]?token|auth[-_]?token|session[-_]?token|bearer|credentials?)($|[._-])/i

/** A JWS compact serialisation: three base64url segments, the first `{"alg"…`. */
export const JWT_VALUE = /^eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]*$/

export const BEARER_VALUE = /^bearer\s+\S+/i

export const REDACTED = "[redacted]"

/** What replaces a presigned URL's path and query; the scheme and host stay. */
export const PRESIGNED = "[presigned]"

/** SigV4 query auth, plain or percent-encoded (a presigned URL nested in another URL's query). */
export const PRESIGNED_MARKER = /X-Amz-(Signature|Credential)(=|%3D)/i

/**
 * A Stripe secret, restricted, publishable or ephemeral key, a webhook
 * secret, or a client secret. The same source as the read-only gateway's
 * `KEY_PATTERN` (pinned equal by the scrubber test); duplicated here because
 * this module is isomorphic and must not import a bounded context.
 */
export const STRIPE_KEY_VALUE = /(sk|rk|pk|ek)_(live|test)_[A-Za-z0-9*]+|whsec_[A-Za-z0-9]+|\b[A-Za-z0-9_]+_secret_[A-Za-z0-9]+/

const STRIPE_KEY_VALUE_GLOBAL = new RegExp(STRIPE_KEY_VALUE.source, "g")

/** An `api.stripe.com` URL that carries a query string or fragment; group 1 is everything before it. */
const STRIPE_API_URL_WITH_QUERY = /(https?:\/\/api\.stripe\.com(?::\d+)?[^\s"'`<>?#]*)[?#][^\s"'`<>]*/gi

/** Pure. Every `api.stripe.com` URL in `text` loses its query and fragment; the rest is untouched. */
export const stripStripeApiQueries = (text: string): string => text.replace(STRIPE_API_URL_WITH_QUERY, "$1")

/** Pure. Presigned URLs cut to host, then Stripe API queries stripped. */
export const redactUrlsInText = (text: string): string => stripStripeApiQueries(redactPresignedUrls(text))

/** Free text (span name, status message): URLs as above, and each Stripe key match replaced in place. */
const redactSpanText = (text: string): string => redactUrlsInText(text).replace(STRIPE_KEY_VALUE_GLOBAL, REDACTED)

/** A URL of any scheme (`https`, `wss`, ...) up to whitespace, a quote or an angle bracket. */
const URL_IN_TEXT = /[a-z][a-z0-9+.-]*:\/\/[^\s"'`<>]+/gi

/** Sentence punctuation a URL match swallows at its end; kept outside the redaction. */
const TRAILING_PUNCTUATION = /[.,;:!?)\]}]+$/

/**
 * Pure. Every presigned URL in `text` becomes `scheme://host/[presigned]`;
 * the rest of the string is untouched. When the marker is present but no URL
 * carries it (a bare query string), there is no host to keep and the whole
 * string becomes {@link PRESIGNED}. An ordinary string comes back as is.
 */
export const redactPresignedUrls = (text: string): string => {
  if (!PRESIGNED_MARKER.test(text)) return text
  const out = text.replace(URL_IN_TEXT, (match) => {
    const trailing = TRAILING_PUNCTUATION.exec(match)?.[0] ?? ""
    const url = trailing === "" ? match : match.slice(0, -trailing.length)
    if (!PRESIGNED_MARKER.test(url)) return match
    const schemeEnd = url.indexOf("://") + 3
    const authority = url.slice(schemeEnd).split(/[/?#]/, 1)[0]!
    const host = authority.slice(authority.lastIndexOf("@") + 1)
    return `${url.slice(0, schemeEnd)}${host}/${PRESIGNED}${trailing}`
  })
  return PRESIGNED_MARKER.test(out) ? PRESIGNED : out
}

/**
 * The attribute value with its presigned URLs cut to host and its Stripe API
 * queries stripped, or `value` itself when it held neither.
 */
const redactUrlValue = (value: AttributeValue | undefined): AttributeValue | undefined => {
  if (typeof value === "string") {
    const out = redactUrlsInText(value)
    return out === value ? value : out
  }
  if (Array.isArray(value) && value.some((item) => typeof item === "string" && redactUrlsInText(item) !== item)) {
    return value.map((item) => (typeof item === "string" ? redactUrlsInText(item) : item)) as AttributeValue
  }
  return value
}

const URL_KEYS: ReadonlySet<string> = new Set(URL_ATTRIBUTE_KEYS)

/** A URL attribute's value as its route template (rule 8); any other key's value as is. */
const templateUrlAttribute = (key: string, value: AttributeValue | undefined): AttributeValue | undefined => {
  if (!URL_KEYS.has(key)) return value
  if (typeof value === "string") return routeTemplateOf(value)
  if (Array.isArray(value)) {
    const out = value.map((item) => (typeof item === "string" ? routeTemplateOf(item) : item))
    return out.every((item, i) => item === value[i]) ? value : (out as AttributeValue)
  }
  return value
}

/** The span `@effect/ai` opens around every tool call: `Effect.fn("Toolkit.handle")` in Toolkit.js. */
export const TOOLKIT_HANDLE_SPAN = "Toolkit.handle"

/**
 * The browser worker's tool names: `browser` then a capital (`browserNavigate`,
 * `browserType`, ...), and the worker's report tool, whose argument is the
 * report text. Matched by NAME rather than imported from the browser-worker
 * barrel, because this module is isomorphic and ships in the browser bundle,
 * and that barrel's module graph is the whole toolkit with Playwright behind
 * it. The prefix also covers a browser tool added later without an edit here;
 * `browser-telemetry-privacy.test.ts` pins every name in the toolkit to it.
 */
export const BROWSER_TOOL_NAME = /^(browser[A-Z][A-Za-z]*|submitBrowserReport)$/

/** What replaces a browser tool's parameters on its `Toolkit.handle` span. */
export const BROWSER_TOOL_PARAMETERS_REDACTED = "[redacted:browser-tool-parameters]"

/** Whether a span is the toolkit's span around one browser tool call. */
const isBrowserToolHandle = (name: string, attributes: Attributes): boolean =>
  name === TOOLKIT_HANDLE_SPAN && typeof attributes.tool === "string" && BROWSER_TOOL_NAME.test(attributes.tool)

/** The span's attributes with a browser tool's `parameters` replaced, or `attributes` itself when there was none. */
const redactBrowserToolParameters = (name: string, attributes: Attributes): Attributes => {
  if (!isBrowserToolHandle(name, attributes)) return attributes
  if (attributes.parameters === undefined || attributes.parameters === BROWSER_TOOL_PARAMETERS_REDACTED) return attributes
  return { ...attributes, parameters: BROWSER_TOOL_PARAMETERS_REDACTED }
}

/**
 * THE FAILED CALL'S TEXT. A browser call the toolkit cannot decode fails its
 * `Toolkit.handle` span with `AiError.MalformedOutput`, whose description
 * quotes the raw parameters ("Failed to decode tool call parameters for tool
 * 'browserType' from: ..."), and `@effect/opentelemetry` writes that error as
 * the status message and as an `exception` event's message and stacktrace
 * (`Cause.pretty`). On such a span both are replaced; the status code and the
 * exception's type stay, so the failure is still counted and classed.
 */
const EXCEPTION_TEXT_KEYS: ReadonlyArray<string> = ["exception.message", "exception.stacktrace"]

const redactBrowserToolExceptionText = (attributes: Attributes | undefined): Attributes | undefined => {
  if (attributes === undefined || !EXCEPTION_TEXT_KEYS.some((key) => attributes[key] !== undefined)) return attributes
  const out: Attributes = { ...attributes }
  for (const key of EXCEPTION_TEXT_KEYS) if (out[key] !== undefined) out[key] = BROWSER_TOOL_PARAMETERS_REDACTED
  return out
}

export interface ScrubResult {
  readonly attributes: Attributes
  /** Keys removed outright. */
  readonly dropped: ReadonlyArray<string>
  /** Keys whose value was replaced with {@link REDACTED} or had a presigned URL cut to its host. */
  readonly redacted: ReadonlyArray<string>
}

const isCredentialShaped = (value: AttributeValue | undefined): boolean =>
  typeof value === "string" && (JWT_VALUE.test(value) || BEARER_VALUE.test(value) || STRIPE_KEY_VALUE.test(value))

/**
 * Pure. Returns a NEW attributes object; the input is never mutated. When
 * nothing needed scrubbing the input object itself is returned, so callers
 * can cheaply detect "unchanged" by identity.
 */
export const scrubAttributes = (attributes: Attributes): ScrubResult => {
  const dropped: Array<string> = []
  const redacted: Array<string> = []
  let out: Record<string, AttributeValue | undefined> | undefined

  for (const key of Object.keys(attributes)) {
    const value = attributes[key]
    if (HEADER_ATTRIBUTE_KEY.test(key)) {
      out ??= { ...attributes }
      delete out[key]
      dropped.push(key)
      continue
    }
    if (SECRET_ATTRIBUTE_KEY.test(key) || isCredentialShaped(value)) {
      out ??= { ...attributes }
      out[key] = REDACTED
      redacted.push(key)
      continue
    }
    const urlRedacted = templateUrlAttribute(key, redactUrlValue(value))
    if (urlRedacted !== value) {
      out ??= { ...attributes }
      out[key] = urlRedacted
      redacted.push(key)
    }
  }

  return { attributes: out ?? attributes, dropped, redacted }
}

/**
 * A copy of `span` carrying scrubbed attributes, or `span` itself when there
 * was nothing to scrub. Prototype and every own property are preserved, so
 * the copy is a `ReadableSpan` in every way the OTLP transformer cares about.
 *
 * The name, the status message and every event (its name and attributes)
 * also have presigned URLs cut to host: `@effect/opentelemetry` records a
 * failing span's error as an `exception` event and as the status message,
 * and turns each `Effect.log` inside a span into an event named by the log
 * line, so an error or log quoting a signed URL reaches the span there.
 * On a browser tool's `Toolkit.handle` span the status message and the
 * exception's message and stacktrace are replaced outright (see
 * `redactBrowserToolExceptionText`).
 */
export const scrubSpan = (span: ReadableSpan): ReadableSpan => {
  const browserHandle = isBrowserToolHandle(span.name, span.attributes)
  const result = scrubAttributes(redactBrowserToolParameters(span.name, span.attributes))
  const name = redactSpanText(span.name)
  const statusMessage =
    span.status.message === undefined
      ? undefined
      : browserHandle
        ? BROWSER_TOOL_PARAMETERS_REDACTED
        : redactSpanText(span.status.message)
  let eventsChanged = false
  const events = span.events.map((event) => {
    const eventName = redactSpanText(event.name)
    const textRedacted = browserHandle && event.name === "exception" ? redactBrowserToolExceptionText(event.attributes) : event.attributes
    const eventAttributes = textRedacted === undefined ? undefined : scrubAttributes(textRedacted).attributes
    if (eventName === event.name && eventAttributes === event.attributes) return event
    eventsChanged = true
    return { ...event, name: eventName, attributes: eventAttributes }
  })

  const overrides: Record<string, unknown> = {}
  if (result.attributes !== span.attributes) overrides.attributes = result.attributes
  if (name !== span.name) overrides.name = name
  if (statusMessage !== span.status.message) overrides.status = { ...span.status, message: statusMessage }
  if (eventsChanged) overrides.events = events
  if (Object.keys(overrides).length === 0) return span

  const clone = Object.create(
    Object.getPrototypeOf(span),
    Object.getOwnPropertyDescriptors(span)
  ) as ReadableSpan
  for (const [key, value] of Object.entries(overrides)) {
    Object.defineProperty(clone, key, { value, enumerable: true, configurable: true, writable: true })
  }
  return clone
}

/**
 * Wrap a `SpanExporter` so nothing it exports carries a transport header, a
 * credential, a presigned URL or a browser tool's raw parameters. Everything
 * else, `forceFlush` and `shutdown`, is delegated.
 */
export const scrubbingSpanExporter = (inner: SpanExporter): SpanExporter => ({
  export: (spans: ReadonlyArray<ReadableSpan>, resultCallback: ExportResultCallback): void =>
    inner.export(spans.map(scrubSpan), resultCallback),
  shutdown: () => inner.shutdown(),
  forceFlush: inner.forceFlush === undefined ? undefined : () => inner.forceFlush!()
})
